import { useEffect, useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import ActionButton from "Components/atoms/buttons/action-button"
import { DownloadIcon, PrintIcon } from "Components/atoms/icons"
import { downloadText, readReportSnapshot, ReportViewKind, runFullExport, tablesToCsv } from "utils/report-snapshot"
import { toast } from "react-toastify"

// Charts animate when drawn; wait before opening the print dialog
const PRINT_DELAY_MS = 800

const PRINT_CSS = `
@page { size: landscape; margin: 10mm; }
.report-view { background: #fff; color: #212529; min-height: 100vh; }
.report-view table { width: 100% !important; table-layout: auto !important; border-collapse: collapse; font-size: 12px; }
.report-view th, .report-view td { border: 1px solid #dee2e6; padding: 4px 6px; white-space: nowrap; width: auto !important; position: static !important; }
.report-view thead th { background: #f1f3f5; }
.report-view .section-container { border: none; box-shadow: none; padding: 0; margin-bottom: 16px; }
@media print {
    body { background: #fff !important; }
    .report-view { padding: 0 !important; }
    .report-view table { font-size: 10px; }
    .report-view tr { break-inside: avoid; }
}
`

interface ReportViewPageProps {
    kind: ReportViewKind
}

// /<report>/print and /<report>/export: no app layout. Renders the snapshot taken by the report
// page (utils/report-snapshot). Print opens the print dialog; Export offers CSV downloads.
const ReportViewPage = ({ kind }: ReportViewPageProps) => {
    const [searchParams] = useSearchParams()
    const snapshotId = searchParams.get("snapshot")
    const snapshot = useMemo(() => readReportSnapshot(snapshotId), [snapshotId])
    const csvTables = useMemo(() => (snapshot && kind === "export" ? tablesToCsv(snapshot.html) : []), [snapshot, kind])

    useEffect(() => {
        document.title = `${snapshot?.title ?? "Report"} - ${kind === "print" ? "Print" : "Export"} | IbexIQ`
    }, [snapshot, kind])

    useEffect(() => {
        if (!snapshot || kind !== "print") return
        const timer = setTimeout(() => window.print(), PRINT_DELAY_MS)
        return () => clearTimeout(timer)
    }, [snapshot, kind])

    if (!snapshot) {
        return <div className="report-view p-4">
            <style>{PRINT_CSS}</style>
            <h4>Report not available</h4>
            <p className="text-muted">This page shows a copy of a report for a few minutes after you open it. Go back to the report and use the {kind === "print" ? "Print" : "Export"} button again.</p>
        </div>
    }

    const baseName = `${snapshot.title} - ${new Date(snapshot.createdAt).toISOString().slice(0, 10)}`
    const handleFullExport = async () => {
        const ok = await runFullExport(snapshotId)
        if (!ok) toast.error("Open the report again and use Export: the report tab was closed or reloaded.")
    }

    const download = (index: number) =>
        downloadText(`${baseName}${csvTables.length > 1 ? ` (${index + 1})` : ""}.csv`, csvTables[index])

    return <div className="report-view p-4">
        <style>{PRINT_CSS}</style>
        <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
            <div>
                <div className="text-muted small">{snapshot.module}</div>
                <h3 className="mb-1">{snapshot.title}</h3>
                <div className="text-muted small">
                    {kind === "print" ? "Printed" : "Exported"} {new Date().toLocaleString("en-CA")}
                </div>
            </div>
            <div className="d-print-none d-flex gap-2 flex-wrap justify-content-end">
                {kind === "print"
                    ? <ActionButton onClick={() => window.print()}>Print / Save as PDF <PrintIcon /></ActionButton>
                    : <>
                        {csvTables.map((_, i) => (
                            <ActionButton key={i} onClick={() => download(i)}>
                                Table CSV{csvTables.length > 1 ? ` ${i + 1}` : ""} <DownloadIcon />
                            </ActionButton>
                        ))}
                        {snapshot.hasFullExport && (
                            <ActionButton onClick={handleFullExport}>Full data CSV <DownloadIcon /></ActionButton>
                        )}
                    </>}
            </div>
        </div>

        {snapshot.filters.length > 0 && (
            <div className="d-flex flex-wrap gap-3 mb-3 small">
                {snapshot.filters.map(f => (
                    <div key={f.label}><span className="text-muted">{f.label}:</span> <strong>{f.value}</strong></div>
                ))}
            </div>
        )}

        {/* Snapshot of the report the user was viewing; controls and handlers are stripped */}
        <div dangerouslySetInnerHTML={{ __html: snapshot.html }} />
    </div>
}

export default ReportViewPage
